// เฝ้าดูว่า <video> มีภาพเดินจริงหรือแค่ค้างดำ — event playing / timeupdate ยิงได้แม้ค้างดำ
// จึงเทียบ currentTime ระหว่าง 2 ครั้งที่วัดแทน

import { useEffect, useRef } from 'react'
import {
  PROGRESS_CHECK_MS,
  PROGRESS_MIN_DELTA,
  STALL_AFTER_MS,
} from './constants'
import { isPageVisible } from './usePageVisible'

const round1 = n => Math.round(n * 10) / 10

// buffered_sec = ภาพที่โหลดรอไว้ข้างหน้าตำแหน่งที่เล่นอยู่กี่วินาที (0 = ค้างรอข้อมูล)
export function sampleOf(video) {
  if (!video) return { video_time: 0, buffered_sec: 0 }
  const t = video.currentTime || 0
  let ahead = 0
  try {
    const ranges = video.buffered
    for (let i = 0; i < ranges.length; i += 1) {
      if (ranges.start(i) <= t && t <= ranges.end(i)) {
        ahead = ranges.end(i) - t
        break
      }
    }
  } catch (e) {
    /* อ่าน buffered ไม่ได้ระหว่างถอด player — ส่ง 0 ไป */
  }
  return { video_time: round1(t), buffered_sec: round1(ahead) }
}

export default function useProgressWatch(videoRef, handlers, enabled) {
  const saved = useRef(handlers)
  saved.current = handlers

  useEffect(() => {
    if (!enabled) return undefined
    const video = videoRef.current
    if (!video) return undefined

    let lastTime = video.currentTime
    let lastAdvanceAt = Date.now()
    let primed = false
    let sawFirstFrame = false
    let stalled = false

    const timer = setInterval(() => {
      const now = Date.now()
      const t = video.currentTime

      // แท็บถูกซ่อน ภาพไม่เดินเพราะเบราว์เซอร์ ไม่ใช่เพราะกล้อง — เริ่มนับใหม่ตอนกลับมาแสดง
      if (!isPageVisible()) {
        lastTime = t
        lastAdvanceAt = now
        return
      }

      // currentTime เชื่อไม่ได้: hls.js seek ไปขอบ live ตอนเริ่ม (แม้ element จะ pause อยู่) / readyState < 2
      // แต่ถ้าเคยเห็นภาพแล้วยังต้องจับค้างต่อ ไม่งั้นค้างขณะ buffer หมดจะไม่มีใครรู้
      const usable = !video.seeking && !video.paused && video.readyState >= 2
      if (!usable) {
        lastTime = t
        if (!sawFirstFrame) lastAdvanceAt = now
        if (sawFirstFrame && !stalled && now - lastAdvanceAt > STALL_AFTER_MS) {
          stalled = true
          saved.current.onStall && saved.current.onStall()
        }
        return
      }

      // ครั้งแรกเก็บค่าตั้งต้นจาก currentTime จริงเท่านั้น (PTS อาจไม่เริ่มที่ 0) — ห้ามตั้ง lastTime = -1
      // ไม่งั้นการวัดครั้งแรกเป็นจริงเสมอ ถูกนับว่ามีภาพทั้งที่ยังไม่ decode สักเฟรม
      if (!primed) {
        primed = true
        lastTime = t
        lastAdvanceAt = now
        return
      }

      if (t > lastTime + PROGRESS_MIN_DELTA) {
        lastTime = t
        lastAdvanceAt = now

        if (!sawFirstFrame) {
          sawFirstFrame = true
          saved.current.onFirstFrame && saved.current.onFirstFrame()
        }
        if (stalled) {
          stalled = false
          saved.current.onResume && saved.current.onResume()
        }
        return
      }

      // ยังไม่เคยเห็นภาพเลย = ยังอยู่สถานะ connecting ไม่ใช่ stalled
      if (!sawFirstFrame) return

      if (!stalled && now - lastAdvanceAt > STALL_AFTER_MS) {
        stalled = true
        saved.current.onStall && saved.current.onStall()
      }
    }, PROGRESS_CHECK_MS)

    return () => clearInterval(timer)
  }, [videoRef, enabled])
}
